import { observer } from 'mobx-react';
import { AppraiserStore } from '../store/appraiserStore';
import React, { ReactNode, useEffect, useState } from 'react';
import {
  DataGrid,
  GridColumnProps,
  SelectFilterComponent,
  SelectFloatingFilterComponent,
  SelectMode,
  StatusChip,
  StatusType,
} from '@roc/ui';
import { makeStyles } from '@material-ui/core/styles';
import { formatDate } from '@roc/feature-utils';
import { Box, Grid, IconButton, Menu } from '@material-ui/core';
import { MoreVert } from '@material-ui/icons';
import { TamariskAppraisalDashboardTab } from '../../tamariskAppraisal/types/types';

const useStyles = makeStyles(theme => ({
  gridContainer: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    height: '65vh',
    overflow: 'hidden',
    width: '100%',
  },
}));

export type AppraiserGridProperties = {
  store: AppraiserStore;
  columns: GridColumnProps[];
  actions: (record, handleMenuClose) => ReactNode;
  toolbar: ReactNode;
};
export const AppraiserGrid = observer(
  ({ store, columns, actions, toolbar }: AppraiserGridProperties) => {
    const classes = useStyles();
    const { appraiserGridStore: gridStore } = store;
    const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);
    const [record, setRecord] = useState<any>({});

    useEffect(() => {
      gridStore.resetAndFetchGridData();
    }, []);

    const frameworkComponents = {
      selectFilter: SelectFilterComponent,
      selectFloatingFilter: SelectFloatingFilterComponent,
      actionCellRenderer: params => {
        return (
          <IconButton
            onClick={e => {
              openMenu(e, params.node.data);
            }}
          >
            <MoreVert color="primary" />
          </IconButton>
        );
      },
      statusCellRenderer: ({ value }) =>
        value ? (
          <StatusChip
            statusType={StatusType.APPRAISER}
            label={value}
            size="small"
          />
        ) : null,
      appraiserNameCellRenderer: params => {
        const appraiserName = params.value;
        return (
          <div
            onClick={() => {
              store.setAppraiser(params.node.data);
              store.addEditOpen = true;
            }}
            style={{
              textDecoration: 'underline',
              color: '#5c94de',
              cursor: 'pointer',
            }}
          >
            {appraiserName}
          </div>
        );
      },
    };

    const onSortChanged = params => {
      const sortModel = params.api.getSortModel();
      gridStore.setSortModel(sortModel);
    };

    const onFilterChanged = params => {
      const filterModel = params.api.getFilterModel();
      gridStore.setFilterModel(filterModel);
    };

    const openMenu = (event: React.MouseEvent<HTMLElement>, record) => {
      setRecord(record);
      setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
      setAnchorEl(null);
    };

    return (
      <Box>
        <Grid item className={classes.gridContainer}>
          <DataGrid
            columns={columns}
            rows={gridStore.gridData.data.rows}
            frameworkComponents={frameworkComponents}
            suppressRowClickSelection={true}
            onSortChanged={onSortChanged}
            onFilterChanged={onFilterChanged}
            isLoading={gridStore.isLoading}
            showFilters={gridStore.showFilters}
            sortModel={gridStore.sortModel}
            filterModel={gridStore.filterModel}
            paginationData={gridStore.gridData.meta}
            setPageNumber={gridStore.setPageNumber}
            toolbar={toolbar}
          />
        </Grid>
        <Menu
          id="menu"
          getContentAnchorEl={null}
          anchorEl={anchorEl}
          keepMounted
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          open={menuOpen}
          onClose={handleMenuClose}
        >
          {actions(record, handleMenuClose)}
        </Menu>
      </Box>
    );
  }
);
